import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { opcoesDaJanelaFiltros } from './documentos-helpers';

interface FiltroDocumentoAvancado {
  modelo: string;
  fornecedor: string;
  numeroDoc: string;
  status: string;
  evento: string;
  chaveNota: string;
}

interface FiltrosModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAplicar: (filtro: FiltroDocumentoAvancado) => void;
  initialFiltro?: Partial<FiltroDocumentoAvancado>;
  tipoDocumento?: '0' | '1'; // 0 = Entrada (Fornecedor), 1 = Saída (Cliente)
  /** O SAT nas Saídas só em período até 2025, como a lista (opcoesDeModelo). */
  exibirSat?: boolean;
}

export function FiltrosModal({
  open,
  onOpenChange,
  onAplicar,
  initialFiltro,
  tipoDocumento = '0',
  exibirSat = false,
}: FiltrosModalProps) {
  const [modelo, setModelo] = useState(initialFiltro?.modelo || 'TODOS');
  const [fornecedor, setFornecedor] = useState(initialFiltro?.fornecedor || '');
  const [numeroDoc, setNumeroDoc] = useState(initialFiltro?.numeroDoc || '');
  const [status, setStatus] = useState(initialFiltro?.status || 'TODOS');
  const [evento, setEvento] = useState(initialFiltro?.evento || 'TODOS');
  const [chaveNota, setChaveNota] = useState(initialFiltro?.chaveNota || '');

  const labelFornecedorCliente = tipoDocumento === '0' ? 'Fornecedor' : 'Cliente';

  useEffect(() => {
    if (open && initialFiltro) {
      setModelo(initialFiltro.modelo || 'TODOS');
      setFornecedor(initialFiltro.fornecedor || '');
      setNumeroDoc(initialFiltro.numeroDoc || '');
      setStatus(initialFiltro.status || 'TODOS');
      setEvento(initialFiltro.evento || 'TODOS');
      setChaveNota(initialFiltro.chaveNota || '');
    }
  }, [open, initialFiltro]);

  const isModeloEspecificoSelecionado = modelo !== 'TODOS';
  // Manifestação do destinatário só existe pra NF-e (55) e CT-e (57).
  const modeloAceitaEvento = modelo === '55' || modelo === '57';

  const handleAplicar = () => {
    const statusNormalizado = status === 'TODOS' ? '' : status;
    const eventoNormalizado = evento === 'TODOS' ? '' : evento;
    // Campo desabilitado não filtra: sem isso, um valor antigo, invisível com "Todos", anulava a busca única.
    onAplicar({
      modelo,
      fornecedor: isModeloEspecificoSelecionado ? fornecedor : '',
      numeroDoc: isModeloEspecificoSelecionado ? numeroDoc : '',
      status: isModeloEspecificoSelecionado ? statusNormalizado : '',
      evento: modeloAceitaEvento ? eventoNormalizado : '',
      chaveNota: isModeloEspecificoSelecionado ? chaveNota : '',
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl min-h-[400px] p-8">
        <DialogHeader>
          <DialogTitle>Filtros Avançados</DialogTitle>
          <DialogDescription>
            {isModeloEspecificoSelecionado
              ? 'Filtre documentos por múltiplos critérios'
              : 'Selecione um modelo específico para habilitar filtros adicionais'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 mt-2">
          <div className="flex flex-col gap-2 col-span-1">
            <Label>Modelo</Label>
            <Select value={modelo} onValueChange={setModelo}>
              <SelectTrigger className="w-full h-10">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos</SelectItem>
                {opcoesDaJanelaFiltros({
                  tipoDocumento,
                  exibirSat,
                  modeloEmVigor: initialFiltro?.modelo,
                }).map(modelo => (
                  <SelectItem key={modelo.value} value={modelo.value} disabled={modelo.foraDaAba}>
                    {modelo.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2 col-span-1">
            <Label className={!isModeloEspecificoSelecionado ? 'text-muted-foreground' : ''}>
              {labelFornecedorCliente}
            </Label>
            <Input
              value={fornecedor}
              onChange={e => setFornecedor(e.target.value)}
              placeholder="Nome ou CNPJ"
              className="w-full h-10"
              disabled={!isModeloEspecificoSelecionado}
            />
          </div>
          <div className="flex flex-col gap-2 col-span-1">
            <Label className={!isModeloEspecificoSelecionado ? 'text-muted-foreground' : ''}>
              Nº do Documento
            </Label>
            <Input
              value={numeroDoc}
              onChange={e => setNumeroDoc(e.target.value)}
              placeholder="Número do Documento"
              className="w-full h-10"
              disabled={!isModeloEspecificoSelecionado}
            />
          </div>
          <div className="flex flex-col gap-2 col-span-1">
            <Label className={!isModeloEspecificoSelecionado ? 'text-muted-foreground' : ''}>
              Status
            </Label>
            <Select
              value={status}
              onValueChange={setStatus}
              disabled={!isModeloEspecificoSelecionado}
            >
              <SelectTrigger className="w-full h-10">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos</SelectItem>
                <SelectItem value="4">Autorizado</SelectItem>
                <SelectItem value="7">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2 col-span-1">
            <Label className={!modeloAceitaEvento ? 'text-muted-foreground' : ''}>Evento</Label>
            <Select value={evento} onValueChange={setEvento} disabled={!modeloAceitaEvento}>
              <SelectTrigger className="w-full h-10">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos</SelectItem>
                <SelectItem value="210210">Ciência da Operação</SelectItem>
                <SelectItem value="210200">Confirmação da Operação</SelectItem>
                <SelectItem value="210220">Desconhecimento da Operação</SelectItem>
                <SelectItem value="210240">Operação Não Realizada</SelectItem>
                <SelectItem value="000">Sem Manifestação</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2 col-span-2">
            <Label className={!isModeloEspecificoSelecionado ? 'text-muted-foreground' : ''}>
              Chave da Nota
            </Label>
            <Input
              value={chaveNota}
              onChange={e => setChaveNota(e.target.value)}
              placeholder="Chave de Acesso da Nota"
              className="w-full h-10"
              disabled={!isModeloEspecificoSelecionado}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button onClick={handleAplicar}>Aplicar Filtros</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
